/**
 * Domain formatters and parsers — the pieces of business logic that get used
 * in 50+ places across web and mobile, so they live in @pdq/core to keep
 * render code dumb and to guarantee web + native compute identically.
 *
 * Pure TypeScript only: no DOM, no React. Safe to import from React Native.
 */

import { format, parseISO } from 'date-fns';

export const DIVISIONS = [
  'WTR', 'MLD', 'REC', 'CON', 'CPO', 'STC', 'ENV', 'ASB', 'TRM', 'TMP', 'REF',
] as const;

export const DIVISION_LABELS: Record<typeof DIVISIONS[number], string> = {
  WTR: 'Water Mitigation',
  MLD: 'Mold Remediation',
  REC: 'Reconstruction',
  CON: 'Contents',
  CPO: 'Contents Packout/Packback',
  STC: 'Structural Cleaning',
  ENV: 'Environmental',
  ASB: 'Asbestos',
  TRM: 'Trauma',
  TMP: 'Temporary Repairs',
  REF: 'Referral',
};

export type DivisionCode = typeof DIVISIONS[number];

export const JOB_NUMBER_RE = /^(\d{2})-(\d{4,5})-([A-Z]{3})$/;

export type ParsedJobNumber = { yy: number; seq: number; division: DivisionCode };

export function parseJobNumber(jobNumber: string): ParsedJobNumber | null {
  const m = JOB_NUMBER_RE.exec(jobNumber.trim());
  if (!m) return null;
  const div = m[3] as DivisionCode;
  if (!DIVISIONS.includes(div)) return null;
  return { yy: Number(m[1]), seq: Number(m[2]), division: div };
}

/**
 * Money formatter — matches the legacy dashboard's `formatMoney` (index.html
 * L11924): always 2 decimal places. Pass `{ withCents: false }` for the rare
 * whole-dollar case (stat tiles use `formatMoneyShort` in legacy).
 */
export function formatMoney(n: number | null | undefined, opts?: { withCents?: boolean }): string {
  if (n == null || Number.isNaN(n)) return '—';
  const cents = opts?.withCents !== false;
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: cents ? 2 : 0,
    maximumFractionDigits: cents ? 2 : 0,
  });
}

/** Legacy `formatMoneyShort` (L11929): `$` + rounded locale string, no cents. */
export function formatMoneyShort(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—';
  return '$' + Math.round(n).toLocaleString('en-US');
}

export function formatDate(d: string | Date | null | undefined, fmt = 'M/d/yyyy'): string {
  if (!d) return '—';
  const date = typeof d === 'string' ? parseISO(d) : d;
  if (Number.isNaN(date.getTime())) return '—';
  return format(date, fmt);
}

export function formatDateTime(d: string | Date | null | undefined): string {
  return formatDate(d, "M/d/yyyy h:mm a");
}

export function daysBetween(a: string | Date | null | undefined, b: string | Date | null | undefined): number | null {
  if (!a || !b) return null;
  const da = typeof a === 'string' ? parseISO(a) : a;
  const db = typeof b === 'string' ? parseISO(b) : b;
  if (Number.isNaN(da.getTime()) || Number.isNaN(db.getTime())) return null;
  return Math.round((db.getTime() - da.getTime()) / 86_400_000);
}

export function divisionLabel(div: string | null | undefined): string {
  if (!div) return '—';
  return DIVISION_LABELS[div as DivisionCode] ?? div;
}

/**
 * Weekday count from `date` up to `now` (catalog `businessDaysSince`, line 291).
 * Excludes Saturdays/Sundays. Used by the SLA day-color bands.
 */
export function businessDaysSince(
  date: string | Date | null | undefined,
  now: Date = new Date(),
): number | null {
  if (!date) return null;
  const start = typeof date === 'string' ? parseISO(date) : date;
  if (Number.isNaN(start.getTime())) return null;
  const d = new Date(start); d.setHours(0, 0, 0, 0);
  const end = new Date(now); end.setHours(0, 0, 0, 0);
  let count = 0;
  while (d < end) {
    d.setDate(d.getDate() + 1);
    const wd = d.getDay();
    if (wd !== 0 && wd !== 6) count += 1;
  }
  return count;
}

/**
 * Title-case an address for display. The Dash export shovels many addresses in
 * ALL CAPS; normalizing at render keeps the hourly mirror untouched. Keeps
 * state abbreviations (NJ), directionals (SW), ordinals (2nd), unit markers
 * and Mc/Mac/O' names sensible. Leaves mixed-case input alone — only shouting
 * (all-upper) or whispering (all-lower) segments are re-cased.
 */
const ADDR_KEEP_UPPER = new Set([
  'NE', 'NW', 'SE', 'SW', 'N', 'S', 'E', 'W', 'PO', 'US',
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS',
  'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NV', 'NH', 'NJ', 'NM', 'NY',
  'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC',
]);
export function titleCaseAddress(input: string | null | undefined): string {
  if (!input) return '';
  const s = input.trim();
  // Mixed-case already? Trust it — only fix ALL-CAPS / all-lower text.
  if (/[a-z]/.test(s) && /[A-Z]/.test(s)) return s;
  return s.replace(/[A-Za-z][A-Za-z']*/g, (word) => {
    const up = word.toUpperCase();
    if (ADDR_KEEP_UPPER.has(up)) return up;
    const lower = word.toLowerCase();
    // Ordinals stay lowercase after the digit (21ST → handled below via digit check).
    const cap = lower.charAt(0).toUpperCase() + lower.slice(1);
    if (/^mc/i.test(lower) && lower.length > 2) return 'Mc' + lower.charAt(2).toUpperCase() + lower.slice(3);
    if (/^o'/i.test(lower) && lower.length > 2) return "O'" + lower.charAt(2).toUpperCase() + lower.slice(3);
    return cap;
  }).replace(/(\d)(St|Nd|Rd|Th)\b/g, (_, d: string, suf: string) => d + suf.toLowerCase());
}
